import { useState } from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import { DateFormats } from 'infrastructure/enums';
import { DateFormatter } from 'infrastructure/functions';
import { showResult } from 'infrastructure/hooks/utils';
import dataService from 'services/dataService';
import {
  apiMedReconAction,
  apiMedReconGetReconciliation,
  apiMedReconImport,
  apiMedReconListReconciliations,
} from 'services/dataService/resources';

import type { IMedication } from 'infrastructure/interfaces';
import type {
  MedReconDiffItem,
  MedReconFieldChange,
  MedReconImportResponse,
  MedReconListResponse,
  MedReconReconciliation,
  MedReconRemediateAction,
} from '../types';

const FIELD_LABELS: Record<string, string> = {
  dosage: 'Dosage',
  route: 'Route',
  frequency: 'Frequency',
  startDate: 'Start date',
  endDate: 'End date',
};

const DATE_FIELDS = new Set(['startDate', 'endDate']);

const formatChangeValue = (value: unknown, field?: string): string => {
  if (value === null || value === undefined || value === '') return 'empty';
  if (field && DATE_FIELDS.has(field)) {
    const parsed = dayjs(String(value));
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : String(value);
  }
  return String(value);
};

const buildUpdatedMessage = (fieldChanges: MedReconFieldChange[]): string => {
  if (!fieldChanges.length) return '';

  return fieldChanges
    .map(({ field, baselineValue, importedValue }) => {
      const fieldLabel = FIELD_LABELS[field] ?? field;
      return `${fieldLabel}: ${formatChangeValue(
        baselineValue,
        field,
      )} -> ${formatChangeValue(importedValue, field)}`;
    })
    .join(', ');
};

const normalizeDate = (date: string | null | undefined): string | null => {
  if (!date) return null;
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('MMM DD YYYY') : null;
};

export const diffItemToMedication = (
  item: MedReconReconciliation | MedReconDiffItem,
): IMedication | null => {
  if (item.diffType.toLowerCase() === 'unchanged') return null;

  const imported = item.importedMedication;
  const baseline = item.baselineMedication;

  if (item.diffType === 'unrecognized') {
    return {
      guid: item.id,
      isActive: imported?.status?.toLowerCase() === 'active',
      name: imported?.medicationName ?? '',
      duration: '',
      dosage: imported?.dosage ?? '',
      frequency: imported?.frequency ?? '',
      route: imported?.route ?? null,
      note: '',
      createdAt: 'createdAt' in item ? item.createdAt : '',
      updatedAt: 'updatedAt' in item ? item.updatedAt : '',
      startDate: normalizeDate(imported?.startDate),
      endDate: normalizeDate(imported?.endDate),
      author: {
        loginGuid: '',
        title: '',
        firstName: '',
        lastName: '',
        profession: '',
      },
      updatedBy: null,
      type: 'unrecognized',
    };
  }

  if (item.diffType === 'removed') {
    return {
      guid: item.id,
      isActive: false,
      name: baseline?.name ?? '',
      duration: '',
      dosage: baseline?.dosage ?? '',
      frequency: baseline?.frequency ?? '',
      route: baseline?.route ?? null,
      note: '',
      createdAt: '',
      updatedAt: '',
      startDate: normalizeDate(baseline?.startDate),
      endDate: normalizeDate(baseline?.endDate),
      author: {
        loginGuid: '',
        title: '',
        firstName: '',
        lastName: '',
        profession: '',
      },
      updatedBy: null,
      type: 'removed',
    };
  }

  return {
    guid: item.id,
    isActive: true,
    name: imported?.medicationName ?? baseline?.name ?? '',
    duration: '',
    dosage: imported?.dosage ?? baseline?.dosage ?? '',
    frequency: imported?.frequency ?? baseline?.frequency ?? '',
    route: imported?.route ?? baseline?.route ?? null,
    note:
      item.diffType === 'updated' ? buildUpdatedMessage(item.fieldChanges) : '',
    createdAt: '',
    updatedAt: '',
    startDate: normalizeDate(imported?.startDate ?? baseline?.startDate),
    endDate: normalizeDate(imported?.endDate ?? baseline?.endDate),
    author: {
      loginGuid: '',
      title: '',
      firstName: '',
      lastName: '',
      profession: '',
    },
    updatedBy: null,
    type: item.diffType.toLowerCase() as 'added' | 'updated',
  };
};

const activeItemsKey = (patientGuid: string) => [
  'medRecon',
  'list',
  patientGuid,
];

const reconciliationDetailKey = (id: string | undefined) => [
  'medRecon',
  'detail',
  id,
];

export const useMedicationReconciliation = (patientGuid: string) => {
  const queryClient = useQueryClient();
  const { data: listData, isLoading: isListLoading } = useQuery({
    queryKey: activeItemsKey(patientGuid),
    queryFn: async () => {
      const { data, error } = await dataService.getList<MedReconListResponse>(
        apiMedReconListReconciliations(patientGuid, {
          status: 'draft',
          limit: 100,
        }),
      );
      if (error) throw new Error(error);
      return data;
    },
    staleTime: 30_000,
  });

  const [overrideReconciliationId, setOverrideReconciliationId] = useState<
    string | undefined
  >(undefined);

  const activeItems =
    (listData as MedReconListResponse | undefined)?.items ?? [];
  const firstActiveItemId: string | undefined = activeItems[0]?.id;

  const activeReconciliationId: string | undefined =
    overrideReconciliationId ?? firstActiveItemId;

  const detailQueries = useQueries({
    queries: activeItems.map((item) => ({
      queryKey: reconciliationDetailKey(item.id),
      queryFn: async () => {
        const { data, error } =
          await dataService.getList<MedReconReconciliation>(
            apiMedReconGetReconciliation(patientGuid, item.id),
          );
        if (error) throw new Error(error);
        return (data ?? null) as MedReconReconciliation | null;
      },
      enabled: !!item.id,
      staleTime: 10_000,
    })),
  });

  const isDetailLoading = detailQueries.some((q) => q.isLoading);
  const detailItems = detailQueries
    .map((q) => q.data)
    .filter((d): d is MedReconReconciliation => d !== null && d !== undefined);

  const allNonUnchangedItems = detailItems.filter(
    (i) => i.diffType.toLowerCase() !== 'unchanged',
  );

  const pendingDiffItems = allNonUnchangedItems.filter(
    (i) => i.remediationAction.toLowerCase() === 'pending',
  );

  const items: IMedication[] = pendingDiffItems
    .map(diffItemToMedication)
    .filter((i): i is IMedication => i !== null);

  const lastSync = detailItems[0]?.createdAt
    ? DateFormatter({
        date: detailItems[0].createdAt,
        format: DateFormats['MMM dd yyyy, hh:mm a'],
      })
    : undefined;

  const source = detailItems[0]?.sourceSystem ?? undefined;

  const { mutateAsync: remediate, isLoading: isRemediating } = useMutation({
    mutationFn: async (actions: MedReconRemediateAction[]) => {
      if (!activeReconciliationId) {
        throw new Error('No active reconciliation found');
      }

      const { error } = await dataService.createOne(
        apiMedReconAction(patientGuid, activeReconciliationId),
        { actions },
      );
      if (error) {
        showResult(error);
        throw new Error(error);
      }
    },
    onSuccess: () => {
      showResult();
      setOverrideReconciliationId(undefined);
      activeItems.forEach((item) => {
        queryClient.invalidateQueries(reconciliationDetailKey(item.id));
      });
      queryClient.invalidateQueries(activeItemsKey(patientGuid));
      queryClient.invalidateQueries({
        queryKey: ['getMedication'],
      });
    },
  });

  const acceptItem = async (itemId: string) => {
    await remediate([{ reconciliationGuid: itemId, action: 'accepted' }]);
  };

  const ignoreItem = async (itemId: string) => {
    await remediate([
      {
        reconciliationGuid: itemId,
        action: 'ignored',
      },
    ]);
  };

  const acceptAll = async (itemIds?: string[]) => {
    const ids =
      itemIds ??
      pendingDiffItems
        .filter((i) => i.diffType.toLowerCase() !== 'unrecognized')
        .map((i) => i.id);
    if (!ids.length) return;
    await remediate(
      ids.map((id) => ({
        reconciliationGuid: id,
        action: 'accepted',
      })),
    );
  };

  const ignoreAll = async (itemIds?: string[]) => {
    const ids = itemIds ?? allNonUnchangedItems.map((i) => i.id);
    if (!ids.length) return;
    await remediate(
      ids.map((id) => ({
        reconciliationGuid: id,
        action: 'ignored',
      })),
    );
  };

  return {
    reconciliation: detailItems[0] ?? null,
    items,
    lastSync,
    source,
    isLoading: isListLoading || isDetailLoading,
    isRemediating,
    activeReconciliationId,
    setActiveReconciliationId: setOverrideReconciliationId,
    acceptItem,
    ignoreItem,
    acceptAll,
    ignoreAll,
  };
};

export const useImportMedications = (patientGuid: string) => {
  const { mutateAsync: importMedications, isLoading: isImporting } =
    useMutation({
      mutationFn: async (data: {
        medications: Array<{
          medicationName: string | null;
          dosage: string | null;
          route: string | null;
          frequency: string | null;
          startDate: string | null;
          endDate: string | null;
          status: string | null;
        }>;
      }): Promise<MedReconImportResponse> => {
        const { error, data: responseData } = await dataService.createOne(
          apiMedReconImport(patientGuid),
          data,
        );
        if (error) {
          throw new Error(error);
        }
        return responseData as MedReconImportResponse;
      },
      onSuccess: () => {
        showResult();
      },
    });

  return {
    importMedications,
    isImporting,
  };
};
